import { select, call, takeLatest, put } from 'redux-saga/effects';

import { WageActions } from '../constants';
import { WageApi } from 'src/services/end-points';
import { StoreState } from '../reducers';
import { PaginationService } from '../../services/api-handlers/pagination';
import { DeleteWageAction, GetWageAction, GetWagesAction, PersistWageAction } from '../actions/wage-actions';
import sortingService from '../../services/sorting';
import { groupWage } from 'src/helpers/wage';
import {
    handleError,
    resolveApiCall,
    ResolverApiFailure,
    ResolverApiSuccess,
} from '../../services/api-handlers/api-resolver';
import { SubmissionError } from 'redux-form';
import { makeFormErrors } from '../../components/redux-form/utils/make-form-errors';
import { push } from 'connected-react-router';
import Router from '../../navigation/router';
import routes from '../../navigation/routes';
import { IWageSend } from '../../types/wage';
import { toast } from 'react-toastify';
import { changeToZeroMoment, changeToZeroMomentDate } from '../../helpers/timeHelpers';

function* handleFetchWages(action: GetWagesAction) {
    const oldData = yield select((state: StoreState) => state.wages.data);
    const { filters, sorting, page, itemsPerPage } = action.payload;
    if (filters && filters['periodEnd[before]']) {
        filters['periodEnd[before]'] = changeToZeroMoment(`${filters['periodEnd[before]'] + ' 23:59:59'}`);
    }
    yield PaginationService.setPageSize(itemsPerPage).resolveApiCall(action, page, oldData, () =>
        WageApi.getWages({
            ...filters,
            order: sortingService.makeOrder(sorting),
            page,
            itemsPerPage,
            pagination: itemsPerPage === -1 ? 0 : 1,
        }),
    );
}

function* handleFetchWage(action: GetWageAction) {
    const {
        payload: { id },
    } = action;

    yield resolveApiCall(action, () => WageApi.getWage(id));
}

function* handleDeleteWage(action: DeleteWageAction) {
    const {
        payload: { id, onSuccess, onError },
    } = action;

    yield resolveApiCall(
        action,
        () => WageApi.deleteWage(id),
        function () {
            if (onSuccess) {
                onSuccess();
            }
        },
        function (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                if (onError) {
                    onError(result.error.message ?? 'Unknown error');
                }
            }
            handleError(result);
        },
    );
}

function* handlePersistWage(action: PersistWageAction) {
    const { payload } = action;
    const {
        data: { id, ...restData },
        callbacks,
    } = payload;
    const groupData: Partial<IWageSend> = groupWage(restData);
    yield resolveApiCall(
        action,
        () => (id ? WageApi.updateWage(id, groupData) : WageApi.createWage(groupData)),
        function* (data: ResolverApiSuccess) {
            yield call(callbacks.resolve);

            toast.success(id ? 'Salary was successfully updated.' : 'Salary was successfully created.');
            if (!id) {
                yield put(push(Router.generate(routes.WAGES_LIST, { param: '' })));
            }
        },
        function* (result: ResolverApiFailure) {
            if (result.error && result.error.response && result.error.response.status) {
                yield call(
                    callbacks.reject,
                    result.error instanceof SubmissionError
                        ? result.error
                        : new SubmissionError({
                              _error: result.error.message ?? 'Incorrect response',
                              ...makeFormErrors(result.error.response?.data),
                          }),
                );
            }
            handleError(result);
        },
    );
}

export default function* () {
    yield takeLatest(WageActions.FETCH_WAGES, handleFetchWages);
    yield takeLatest(WageActions.FETCH_WAGE, handleFetchWage);
    yield takeLatest(WageActions.DELETE_WAGE, handleDeleteWage);
    yield takeLatest(WageActions.PERSIST_WAGE, handlePersistWage);
}
